import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'

import style from './MyhouseMainTradePrice.module.scss'

import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import MyhouseTooltipLayer from '@Components/atoms/MyhouseTooltipLayer'
import IconPhone16x18 from '@Root/ui/images/myhouse/IconPhone16x18.svg'
import IconEstate16x18 from '@Root/ui/images/myhouse/IconEstate16x18.svg'
import IconCloseTooltip from '@Root/ui/images/myhouse/IconCloseTooltip.svg'
import ArrowTooltip from '@Root/ui/images/myhouse/ArrowTooltip.svg'
import ArrowRight7x12 from '@Root/ui/images/myhouse/ArrowRight7x12.svg'
import useModal from '@Hooks/myhouse/useModal'
import { HouseDetail } from '@Root/slices/myhouse/mainSlice'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import SvgMyhouseSettingAuth from '@Ui/images/myhouse/myhouse_setting_auth.svg'
import { TRADE } from '@Root/contants'
import useBrowser from '@Hooks/myhouse/useBrowser'
import { handleNclick, handleNclickMix } from '@Helpers/nclick'
import { NCLICK } from '@Root/contants/lcs'

const cx = className.bind(style)

type Props = {
  houseDetail: HouseDetail
  tradeTypeCode?: any
  isRegisteredDongHo?: boolean
}

const MyhouseMainTradePrice: React.FC<Props> = ({ houseDetail, tradeTypeCode, isRegisteredDongHo }: Props) => {
  const [isAddressTooltipOpen, setIsAddressTooltipOpen] = useState(false)
  const { open } = useModal()
  const { isMobile, isIpad } = useBrowser()
  const detailAddressTooltipKey = houseDetail?.houseNo + '_addressTooltip'

  const onClickPhoneNo = (event: React.MouseEvent<HTMLButtonElement>) => {
    handleNclick(NCLICK.MY_HOUSE.MAIN_INFO.OFFICE, event)
    if (houseDetail.phoneList.length === 1) {
      window.location.href = `tel:${houseDetail.phoneList[0]}`
    } else {
      open(MODAL_TYPE.MANAGEMENT_PHONE, {
        title: `${houseDetail.complexName}\n관리사무소`,
        phoneList: houseDetail.phoneList,
      })
    }
  }

  const handleCloseAddressTooltip = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    handleNclick(NCLICK.MY_HOUSE.MAIN_INFO.DETAIL_ADDRESS_TOOLTIP_CLOSE, event)
    // event.nativeEvent.stopImmediatePropagation()
    localStorage.setItem(detailAddressTooltipKey, 'close')

    setIsAddressTooltipOpen(false)
  }

  const onClickComplexInfo = (event: React.MouseEvent<HTMLButtonElement>) => {
    handleNclick(NCLICK.MY_HOUSE.MAIN_INFO.COMPLEX_INFO, event)

    if (isMobile || isIpad) {
      window.location.href = `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${houseDetail.complexNo}`
    } else {
      window.location.href = `${process.env.REACT_APP_SPACE_SERVICE_URL}/complexes/${houseDetail.complexNo}`
    }
  }

  useEffect(() => {
    const isTooltip = Boolean(
      (!houseDetail?.buildName || !houseDetail?.hoName) &&
        houseDetail?.isTrendPriceExist &&
        isRegisteredDongHo &&
        tradeTypeCode === TRADE.DEAL &&
        localStorage.getItem(detailAddressTooltipKey) !== 'close',
    )
    setIsAddressTooltipOpen(isTooltip)
  }, [houseDetail])

  if (!houseDetail) return <></>

  return (
    <div className={cx('article')}>
      <div className={cx('header')}>
        <div className={cx('title')}>
          <span className={cx('text')}>{houseDetail.tradeType}</span>
          {/*{houseDetail.tradeType === '매매 시세' && (*/}
          {tradeTypeCode === TRADE.DEAL && !!houseDetail.purchasePrice && (
            <MyhouseTooltipLayer
              title={'시세 기준'}
              description={
                houseDetail.provider === 'trendPrice'
                  ? '한국부동산원 부동산테크에서 발표한 호별 시세 상한가/하한가의 평균값입니다.'
                  : houseDetail.provider === 'kab'
                  ? '한국부동산원에서 발표한 상한가/하한가의 평균값입니다.'
                  : houseDetail.provider === 'kbstar'
                  ? 'KB부동산에서 발표한 일반평균가입니다.'
                  : ''
              }
              nclickOpen={NCLICK.MY_HOUSE.MAIN_INFO.PRICE_TOOLTIP}
            />
          )}
        </div>
      </div>
      <strong className={cx('price')}>
        {houseDetail.price}
        {
          // 하락인 경우 is_decrease 클래스 추가
          houseDetail.variationStr && (
            <em className={cx('compare', { is_decrease: houseDetail.variation < 0 })}>
              {houseDetail.variationStr} {houseDetail.variation < 0 ? '하락' : '상승'}
            </em>
          )
        }
      </strong>
      <div className={cx('detail')}>
        {houseDetail.ownerAuth && (
          <span className={cx('auth')}>
            <SvgMyhouseSettingAuth />
            집주인인증
          </span>
        )}
        {!houseDetail.ownerAuth && houseDetail.residentAuth && (
          <span className={cx('auth')}>
            <SvgMyhouseSettingAuth />
            입주민인증
          </span>
        )}
        {!houseDetail.ownerAuth && !houseDetail.residentAuth && <span className={cx('item')}>{houseDetail.assetType}</span>}
        {houseDetail.detailAddress && <span className={cx('item')}>{houseDetail.detailAddress}</span>}
        {houseDetail.homeSize && <span className={cx('item')}>{houseDetail.homeSize}㎡</span>}
        {(!houseDetail.detailAddress || ((!houseDetail.buildName || !houseDetail.hoName) && houseDetail.isTrendPriceExist)) && (
          <span className={cx('detail_address', { 'is-active': isAddressTooltipOpen })}>
            <a
              className={cx('input')}
              href={`${LAND_SERVICE_PREFIX_URL.MYHOUSE_REGISTER}/main/${houseDetail.houseNo}`}
              onClick={handleNclickMix(NCLICK.MY_HOUSE.MAIN_INFO.DETAIL_ADDRESS)}
            >
              상세주소 입력
              <ArrowRight7x12 />
              {isAddressTooltipOpen && <ArrowTooltip />}
            </a>
            {isAddressTooltipOpen && (
              <>
                <span className={cx('layer')}>
                  상세주소(동, 호수)를 입력하면
                  <br /> 호별시세를 확인하실 수 있습니다.
                  <button type="button" id={'trend_price_close_button'} className={cx('button_close')} onClick={handleCloseAddressTooltip}>
                    <IconCloseTooltip />
                    <span className="blind">닫기</span>
                  </button>
                </span>
              </>
            )}
          </span>
        )}
      </div>
      <div className={cx('detail')}>
        {houseDetail.purchasePrice && (
          <span className={cx('item')}>
            매입가 <strong className={cx('detail_price')}>{houseDetail.purchasePrice}</strong>
          </span>
        )}
        {houseDetail.contractDate ? (
          <span className={cx('item')}>
            {houseDetail.contractDate}
            {tradeTypeCode === TRADE.DEAL ? '.매입' : '.입주'}
          </span>
        ) : (
          <a
            className={cx('input')}
            href={`${LAND_SERVICE_PREFIX_URL.MYHOUSE_REGISTER}/main/${houseDetail.houseNo}`}
            onClick={handleNclickMix(tradeTypeCode === TRADE.DEAL ? NCLICK.MY_HOUSE.MAIN_INFO.BUY_DATE : NCLICK.MY_HOUSE.MAIN_INFO.MOVE_DATE)}
          >
            {tradeTypeCode === TRADE.DEAL ? '매입일 입력' : '입주일 입력'}
            <ArrowRight7x12 />
          </a>
        )}
      </div>
      {houseDetail.Deposit && houseDetail.Deposit !== '0' && (
        <div className={cx('detail')}>
          <span className={cx('item')}>보증금 {houseDetail.Deposit}</span>
        </div>
      )}
      {houseDetail.ResalePopupInfo && (
        <div className={cx('limited')}>
          <span className={cx('description')}>본 단지는 전매제한 단지입니다.</span>
          <MyhouseTooltipLayer
            title={'전매제한 단지'}
            description={
              '본 단지는 전매제한 단지로, 일반 분양권은 전매제한 종료 이후 거래가 가능합니다. 전매제한 분양권을 매매할 경우 주택법에 따라 처벌될 수 있으므로 전매제한 종료 이후 거래하시기 바랍니다.'
            }
            isInfo={`${houseDetail.ResalePopupInfoDate}`}
            nclickOpen={NCLICK.MY_HOUSE.MAIN_INFO.RESALE_TOOLTIP}
          />
        </div>
      )}
      {houseDetail.someAreaResalePopupInfo && (
        <div className={cx('limited')}>
          <span className={cx('description')}>본 단지는 일부 면적 전매제한 단지입니다.</span>
          <MyhouseTooltipLayer
            title={'일부 면적 전매제한 단지'}
            description={
              '본 단지는 일부 면적 전매제한 단지로, 일반 분양권은 전매제한 종료 이후 거래가 가능합니다. 전매제한 분양권을 매매할 경우 주택법에 따라 처벌될 수 있으므로 전매제한 종료 이후 거래하시기 바랍니다.'
            }
            isInfo={`${houseDetail.ResalePopupInfoDate}`}
          />
        </div>
      )}

      {(houseDetail.phoneList?.length > 0 || houseDetail.complexInfoUrl) && (
        <div className={cx('button_area')}>
          {!isMobile && !isIpad ? (
            <>
              {houseDetail.phoneList && (
                <div className={cx('phonelist')}>
                  <span className={cx('text')}>관리사무소</span>
                  {houseDetail.phoneList.map((phone, index) => (
                    <span className={cx('tel')} key={`phone_${index}`}>
                      {phone}
                    </span>
                  ))}
                </div>
              )}
              <div className={cx('inner')}>
                {houseDetail.complexInfoUrl && (
                  <button type="button" className={cx('button_inner')} onClick={onClickComplexInfo}>
                    <IconEstate16x18 />
                    단지정보
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className={cx('inner')}>
              {houseDetail.phoneList?.length > 0 && (
                <button type="button" className={cx('button_inner', 'type_office')} onClick={onClickPhoneNo}>
                  <IconPhone16x18 />
                  관리사무소
                </button>
              )}
              {houseDetail.complexInfoUrl && (
                <button type="button" className={cx('button_inner')} onClick={onClickComplexInfo}>
                  <IconEstate16x18 />
                  단지정보
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MyhouseMainTradePrice
